/**
 * 图片处理工具函数
 */
import { getProxyImageUrl } from '@/api/image'

/**
 * 处理商品图片URL，解决跨域问题（仅用于截图功能）
 * @param {string} originalUrl - 原始图片URL
 * @returns {Promise<string>} 代理后的图片URL
 */
export const processProductImageUrl = async (originalUrl) => {
  if (!originalUrl) {
    return ''
  }
  
  // 如果已经是代理后的URL，直接返回
  if (originalUrl.includes('mall.sainbainu.com') || originalUrl.includes('image.sainbainu.com')) {
    return originalUrl
  }
  
  // 如果是本地资源，直接返回
  if (originalUrl.startsWith('/') || originalUrl.startsWith('./') || originalUrl.startsWith('../')) {
    return originalUrl
  }
  
  // 如果是跨域URL，调用代理接口（仅用于截图功能）
  if (originalUrl.startsWith('http://') || originalUrl.startsWith('https://')) {
    try {
      console.log('调用代理接口获取图片URL（用于截图）:', originalUrl)
      const { data } = await getProxyImageUrl(originalUrl)
      
      if (data && data.url) {
        console.log('代理接口返回新URL:', data.url)
        return data.url
      } else {
        console.warn('代理接口返回无效数据')
        return originalUrl
      }
    } catch (error) {
      console.error('调用代理接口失败:', error)
      return originalUrl
    }
  }
  
  return originalUrl
}

/**
 * 同步版本的图片URL处理函数（用于模板绑定和普通显示）
 * @param {string} originalUrl - 原始图片URL
 * @returns {string} 原始图片URL（同步版本不调用代理接口）
 */
export const processProductImageUrlSync = (originalUrl) => {
  if (!originalUrl) {
    return ''
  }
  
  // 同步版本直接返回原始URL，不调用代理接口
  // 只有截图功能才需要调用代理接口
  return originalUrl
}

/**
 * 批量获取代理图片URL
 * @param {Array<string>} urls - 图片URL数组
 * @returns {Promise<Array<string>>} 代理后的图片URL数组
 */
export const getProxyImageUrls = async (urls) => {
  if (!Array.isArray(urls)) {
    return []
  }
  
  const promises = urls.map(url => processProductImageUrl(url))
  return Promise.all(promises)
}

/**
 * 处理分类图片URL（同步版本，不调用代理接口）
 * @param {string} originalUrl - 原始图片URL
 * @returns {string} 处理后的图片URL
 */
export const processCategoryImageUrl = (originalUrl) => {
  if (!originalUrl) {
    return ''
  }
  
  // 同步版本直接返回原始URL，不调用代理接口
  // 只有截图功能才需要调用代理接口
  return originalUrl
}

/**
 * 处理协议书图片URL（同步版本，不调用代理接口）
 * @param {string} originalUrl - 原始图片URL
 * @returns {string} 处理后的图片URL
 */
export const processAgreementImageUrl = (originalUrl) => {
  if (!originalUrl) {
    return ''
  }
  
  // 同步版本直接返回原始URL，不调用代理接口
  // 只有截图功能才需要调用代理接口
  return originalUrl
}

/**
 * 检查图片URL是否需要代理
 * @param {string} url - 图片URL
 * @returns {boolean} 是否需要代理
 */
export const needsProxy = (url) => {
  if (!url) return false
  
  // 本地资源不需要代理
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
    return false
  }
  
  // 已经是代理URL不需要再次代理
  if (url.includes('/api/image-proxy')) {
    return false
  }
  
  // 跨域URL需要代理
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return true
  }
  
  return false
}